import { createContext, useCallback, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ToastVariant = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
}

export interface ToasterContextValue {
  toast: (message: string, variant?: ToastVariant) => void;
}

// eslint-disable-next-line react-refresh/only-export-components -- context is intentionally co-located with provider
export const ToasterContext = createContext<ToasterContextValue | undefined>(undefined);

let nextId = 1;

export function ToasterProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, variant: ToastVariant = 'info') => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, message, variant }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4500);
  }, []);

  const dismiss = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToasterContext.Provider value={{ toast }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto flex items-start gap-2 rounded-md bg-bg-card px-3 py-2 text-sm shadow-lg',
              t.variant === 'success' && 'border-l-4 border-status-published',
              t.variant === 'error' && 'border-l-4 border-status-declining',
              t.variant === 'info' && 'border-l-4 border-accent',
            )}
          >
            {t.variant === 'success' && (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-status-published" />
            )}
            {t.variant === 'error' && (
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-status-declining" />
            )}
            {t.variant === 'info' && (
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
            )}
            <span className="flex-1 text-text-primary">{t.message}</span>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              className="text-text-muted hover:text-text-primary"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ToasterContext.Provider>
  );
}
